import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CheckCircleIcon,
  HandThumbUpIcon,
  MapPinIcon,
  CurrencyPoundIcon,
  ArrowRightStartOnRectangleIcon,
  Squares2X2Icon,
  ArrowRightEndOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import Button from './Button';
import { useFlow, FlowType } from '../utilities/useFlow';
import { axiosKK } from '../services/networkRequest';
import { adminCheck } from '../utilities/Role';
import { getUser, clearUser } from '../services/storage';

// Heroicons are forwardRef components, not plain function components, so
// React.ComponentType<SVGProps> does not accept them. Every icon in the set
// shares one signature — borrow it rather than restate it.
type IconComponent = typeof CheckCircleIcon;

// Ties the toggle's aria-controls to the panel it discloses.
const MENU_ID = 'site-header-menu';

interface NavLink {
  key: string;
  label: string;
  href: string;
  /**
   * External destinations open in a new tab. Internal ones are SPA routes and
   * render as <Link> — the previous header opened `/storage/boxes` with
   * target="_blank", which cold-booted the app into a second tab and threw
   * away any in-progress booking.
   */
  external: boolean;
  /** First path segment that marks this link as the current page. */
  activeSegment?: string;
}

interface TrustSignal {
  key: string;
  label: string;
  Icon: IconComponent;
  /**
   * Drop order for the trust strip. 1 survives longest; higher numbers fall
   * away first as the strip runs out of room. The breakpoints themselves live
   * in `_header.scss` next to the layout they govern.
   */
  priority: 1 | 2 | 3 | 4;
}

function navLinks(flow: FlowType): NavLink[] {
  if (flow === 'Keepr') {
    return [
      {
        key: 'storage',
        label: 'Storage',
        href: 'https://keepr.co',
        external: true,
      },
      {
        key: 'support',
        label: 'Support',
        href: 'https://keepr.co/#faqs',
        external: true,
      },
    ];
  }

  if (flow === 'St Annes') {
    return [
      {
        key: 'storage',
        label: 'Storage',
        href: 'https://www.st-annes.ox.ac.uk',
        external: true,
      },
      {
        key: 'support',
        label: 'Support',
        href: 'https://www.st-annes.ox.ac.uk/current-students/',
        external: true,
      },
    ];
  }

  return [
    {
      key: 'storage',
      label: 'Storage',
      href: '/storage/boxes',
      external: false,
      activeSegment: 'storage',
    },
    {
      key: 'shipping',
      label: 'Shipping',
      href: '/shipping/boxes',
      external: false,
      // Previously keyed on 'suitcase', which this link can never navigate to,
      // so the Shipping tab could not light up on its own route.
      activeSegment: 'shipping',
    },
    {
      key: 'news',
      label: 'News',
      href: 'https://www.kitkeeper.co.uk/blog/',
      external: true,
    },
    {
      key: 'support',
      label: 'Support',
      href: 'https://www.kitkeeper.co.uk/#faqs',
      external: true,
    },
  ];
}

function contactHref(flow: FlowType): string {
  if (flow === 'Keepr') return 'https://keepr.co/#contact';
  if (flow === 'St Annes') return 'https://www.st-annes.ox.ac.uk/contact/';
  return 'https://www.kitkeeper.co.uk/contact';
}

const TRUST_SIGNALS: TrustSignal[] = [
  {
    key: 'pricing',
    label: 'Flexible pricing',
    Icon: CheckCircleIcon,
    priority: 1,
  },
  {
    key: 'cover',
    label: 'Free £100 cover',
    Icon: HandThumbUpIcon,
    priority: 2,
  },
  {
    key: 'innovate',
    label: 'Funded by Innovate UK',
    Icon: CurrencyPoundIcon,
    priority: 3,
  },
  {
    key: 'coverage',
    label: 'Collect & deliver anywhere in the UK',
    Icon: MapPinIcon,
    priority: 4,
  },
];

function Brand({ flow }: { flow: FlowType }): JSX.Element {
  if (flow === 'Keepr') {
    return (
      <Link to="/" className="site-header__brand site-header__brand--keepr">
        <img
          src="/img/svg/keepr.svg"
          alt="Keepr"
          className="site-header__logo"
        />
      </Link>
    );
  }

  if (flow === 'St Annes') {
    return (
      <Link to="/" className="site-header__brand site-header__brand--stannes">
        <img
          src="/img/svg/st-annes-fullcolour-WHITE-RGB.svg"
          alt="St Anne's College, Oxford"
          className="site-header__logo"
        />
      </Link>
    );
  }

  return (
    <Link to="/" className="site-header__brand site-header__brand--kitkeeper">
      {/* Intrinsic size (1281 × 272) declared so the browser reserves the box
          and the bar does not shift as the logo decodes. */}
      <img
        src="/img/Kit Keeper Logo - White.png"
        width={185}
        height={39}
        alt="Kit Keeper"
        className="site-header__logo"
      />
    </Link>
  );
}

interface HeaderAuthProps {
  isLoggedIn: boolean;
  dashboardPath: string;
  dashboardLabel: string;
  onLogout: () => void;
  /** Dismisses the mobile disclosure panel once a destination is chosen. */
  onNavigate: () => void;
}

/**
 * One auth affordance for every breakpoint. Desktop lays the icon and label
 * out in a row; from $largeTablet down the same markup stacks the label under
 * the icon; from $tablet down it moves inside the disclosure panel and goes
 * back to a full-width row. The previous header shipped two separate copies
 * of this markup (`.header-login-btn` and `.header-mobile-auth-btn`) that had
 * to be kept in step by hand.
 */
function HeaderAuth({
  isLoggedIn,
  dashboardPath,
  dashboardLabel,
  onLogout,
  onNavigate,
}: HeaderAuthProps): JSX.Element {
  return (
    <ul className="site-header__auth">
      {isLoggedIn && (
        <li className="site-header__auth-item">
          <Link
            to={dashboardPath}
            className="site-header__auth-link"
            onClick={onNavigate}
          >
            <Squares2X2Icon
              className="site-header__auth-icon"
              aria-hidden="true"
            />
            <span className="site-header__auth-label">{dashboardLabel}</span>
          </Link>
        </li>
      )}

      <li className="site-header__auth-item">
        {isLoggedIn ? (
          <button
            type="button"
            onClick={onLogout}
            className="site-header__auth-link"
          >
            <ArrowRightStartOnRectangleIcon
              className="site-header__auth-icon"
              aria-hidden="true"
            />
            <span className="site-header__auth-label">Log out</span>
          </button>
        ) : (
          <Link
            to="/login"
            className="site-header__auth-link"
            onClick={onNavigate}
          >
            <ArrowRightEndOnRectangleIcon
              className="site-header__auth-icon"
              aria-hidden="true"
            />
            <span className="site-header__auth-label">Log in</span>
          </Link>
        )}
      </li>
    </ul>
  );
}

export default function Header(): JSX.Element {
  const { pathname } = useLocation();
  const flow = useFlow();
  const user = getUser();
  const isLoggedIn = !!user;
  const isAdmin = user ? adminCheck(user) : false;
  const dashboardPath = isAdmin ? '/admin/dashboard' : '/dashboard';
  const links = navLinks(flow);
  const currentSegment = pathname?.split('/')[1] ?? '';

  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const handleLogout = () => {
    clearUser();
    delete axiosKK.defaults.headers.common['Authorization'];
    window.location.assign('/');
  };

  useEffect(() => {
    window.fbq('track', 'PageView');
  }, [pathname]);

  // A route change means the panel has done its job.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Escape and outside-click dismissal, per the WAI-ARIA disclosure pattern.
  // This is a disclosure, not a modal dialog: the page behind stays reachable,
  // so it needs no focus trap and no scroll lock — but Escape must return
  // focus to the control that opened it.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // The toggle runs its own onClick; swallowing it here would re-open the
      // panel on the same tap.
      if (toggleRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [menuOpen]);

  return (
    <header className="site-header">
      <div className="site-header__bar">
        <Brand flow={flow} />

        <div className="site-header__actions">
          {/* Sits before the panel in DOM order so a keyboard user who
              activates it tabs straight into the disclosed links. */}
          <button
            type="button"
            ref={toggleRef}
            className="site-header__menu-toggle"
            aria-expanded={menuOpen}
            aria-controls={MENU_ID}
            // Stable accessible name: aria-expanded already announces the
            // state, so flipping the label to "Close menu" would have a
            // screen reader read "Close menu, expanded".
            aria-label="Menu"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <XMarkIcon
                className="site-header__menu-toggle-icon"
                aria-hidden="true"
              />
            ) : (
              <Bars3Icon
                className="site-header__menu-toggle-icon"
                aria-hidden="true"
              />
            )}
          </button>

          {/* One DOM home for the nav and the auth actions. At >= $tablet this
              wrapper is `display: contents`, so both render inline in the bar
              exactly as they did; below it the same wrapper becomes the
              disclosure panel. Sharing the markup is what stops the two
              layouts drifting apart — the pre-redesign header kept a second
              hand-maintained copy for mobile. */}
          <div
            id={MENU_ID}
            ref={menuRef}
            className="site-header__menu"
            data-open={menuOpen}
          >
            <nav className="site-header__nav" aria-label="Primary">
              <ul className="site-header__nav-list">
                {links.map(({ key, label, href, external, activeSegment }) => {
                  const isCurrent =
                    !!activeSegment && activeSegment === currentSegment;

                  return (
                    <li key={key} className="site-header__nav-item">
                      {external ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="site-header__nav-link"
                          onClick={closeMenu}
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          to={href}
                          className="site-header__nav-link"
                          aria-current={isCurrent ? 'page' : undefined}
                          onClick={closeMenu}
                        >
                          {label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            <HeaderAuth
              isLoggedIn={isLoggedIn}
              dashboardPath={dashboardPath}
              dashboardLabel={isAdmin ? 'Dashboard' : 'Account'}
              onLogout={handleLogout}
              onNavigate={closeMenu}
            />
          </div>

          <Button
            as="a"
            href={contactHref(flow)}
            target="_blank"
            rel="noreferrer"
            variant="inverse"
            size="md"
            className="site-header__cta"
          >
            Contact Us
          </Button>
        </div>
      </div>

      <div className="site-header__strip">
        <ul className="site-header__signals">
          {TRUST_SIGNALS.map(({ key, label, Icon, priority }) => (
            <li
              key={key}
              className="site-header__signal"
              data-priority={priority}
            >
              <Icon className="site-header__signal-icon" aria-hidden="true" />
              {label}
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}
